import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';

export interface SeoData {
  title: string;
  description: string;
  /** Path relative to the site root, e.g. '/pricing'. Defaults to '/'. */
  path?: string;
  /** Absolute image URL used for social share previews. */
  image?: string;
  /** Set to 'noindex' for pages that shouldn't appear in search results. */
  robots?: string;
}

/** Marketing site root domain — same `erpDomain` value used across every environment file. */
export const SITE_URL = `https://${environment.erpDomain}`;
const DEFAULT_IMAGE = `${SITE_URL}/images/mobilizers/orderyaa-erp.png`;
const SITE_NAME = 'orderYaa ERP';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  /** Sets title, meta description, canonical URL, and Open Graph / Twitter tags for the current page. */
  update(data: SeoData): void {
    const path = data.path ?? '/';
    const url = `${SITE_URL}${path}`;
    const image = data.image ?? DEFAULT_IMAGE;
    const fullTitle = data.title.includes(SITE_NAME) ? data.title : `${data.title} | ${SITE_NAME}`;

    this.title.setTitle(fullTitle);

    this.meta.updateTag({ name: 'description', content: data.description });
    this.meta.updateTag({ name: 'robots', content: data.robots ?? 'index, follow' });

    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    this.meta.updateTag({ property: 'og:description', content: data.description });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: fullTitle });
    this.meta.updateTag({ name: 'twitter:description', content: data.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonical(url);
  }

  /** Injects a JSON-LD structured data block. Pass a unique id so repeated calls replace it instead of stacking. */
  setStructuredData(id: string, data: object): void {
    const existing = this.document.getElementById(id);
    if (existing) {
      existing.remove();
    }
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  private setCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
